import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/i18n';
import { DeviceType } from '../../models/device';
import { geoListUrl, signInPath, weatherListUrl } from '../../routes.constants';
import useAuthStore from '../../stores/auth.store';
import useDevicesStore from '../../stores/devices.store';
import Logo from '../general/Logo';
import { LogoutIcon, PinIcon, WeatherIcon } from '../icons/Icons';
import './Sidebar.scss';

const initials = (name?: string) => {
  const parts = (name ?? '').split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase() || '··';
};

const Sidebar = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { username, email, logout } = useAuthStore();
  const { counts, fetchCounts } = useDevicesStore();

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const items = [
    { to: weatherListUrl, label: t('nav.weather'), icon: <WeatherIcon />, count: counts[DeviceType.WEATHER] },
    { to: geoListUrl, label: t('nav.geo'), icon: <PinIcon />, count: counts[DeviceType.GEO] },
  ];

  const onLogout = async () => {
    await logout();
    navigate(signInPath, { replace: true });
  };

  return (
    <nav aria-label={t('nav.label')} className="Sidebar">
      <div className="Sidebar__brand">
        <Logo />
      </div>
      <div className="Sidebar__section">
        <p className="Sidebar__caption">{t('nav.devices')}</p>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `Sidebar__item${isActive ? ' Sidebar__item--active' : ''}`}
          >
            {item.icon}
            {item.label}
            {item.count !== undefined && <span className="Sidebar__badge">{item.count}</span>}
          </NavLink>
        ))}
      </div>
      <div className="Sidebar__user">
        <div aria-hidden="true" className="Sidebar__avatar">
          {initials(username ?? email)}
        </div>
        <div className="Sidebar__who">
          <span className="Sidebar__name">{username ?? email}</span>
          <span className="Sidebar__session">
            {username && email && username !== email ? email : t('user.session')}
          </span>
        </div>
        <button
          type="button"
          aria-label={t('user.signOut')}
          title={t('user.signOut')}
          className="Sidebar__logout"
          onClick={onLogout}
        >
          <LogoutIcon />
        </button>
      </div>
    </nav>
  );
};

export default Sidebar;
